// @ts-nocheck
// Three.js:
import * as THREE from 'three';

// Global:
import { globalState } from '../../Global/global_variables';
import { GeometryUtils } from '../../data/geometry/GeometryUtils';
import { getGrid } from '../../store/LoadingStore';
import { getViewer } from '../../store/viewerStore';
import { DrawModule } from './DrawingModule';
import { polygonHasSelfIntersection } from '@data/geometry/Functions';
import FVector from '@data/geometry/FVector';
import { getGlobalState } from '../../Gui/store/persistent/persistentStore';

export class EventHandler {
    private drawModule: DrawModule;
    private newPointRadius: number;

    private raycaster: THREE.Raycaster;
    private mouse: THREE.Vector2;

    private lastClickTime = 0;

    private suppressNextClick = false;
    private suppressUntil = 0;

    private _onMouseMove: (event: MouseEvent) => void;
    private _onMouseDown: (event: MouseEvent) => void;
    private _onMouseUp: (event: MouseEvent) => void;

    private _onKeyDown: (event: KeyboardEvent) => void;

    private onTransformChange: (() => void) | null = null;
    private onTransformEnd: (() => void) | null = null;

    // Movement tracking:
    private mouseDownPos: { x: number; y: number } | null = null;
    private movedTooMuch = false;
    private moveThreshold = 3; //px
    private mouseDownFromUi = false;

    constructor(drawModule: DrawModule) {
        this.drawModule = drawModule;
        this.newPointRadius = 1;

        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();

        this._onMouseMove = this.onMouseMove.bind(this);
        this._onMouseDown = this.onMouseDown.bind(this);
        this._onMouseUp = this.onMouseUp.bind(this);

        this._onKeyDown = this.onKeyDown.bind(this);

        this.attachEventListeners();
    }

    dispose(): void {
        this.deactivateTransformControls();
        this.detachEventListeners();
    }

    // =====================================
    // LIFECYCLE
    // =====================================

    private listenersAttached = false;

    attachEventListeners(): void {
        if (this.listenersAttached) return;
        window.addEventListener('mousemove', this._onMouseMove, false);
        window.addEventListener('mousedown', this._onMouseDown, false);
        window.addEventListener('mouseup', this._onMouseUp, false);
        window.addEventListener('keydown', this._onKeyDown, false);
        this.listenersAttached = true;
    }

    detachEventListeners(): void {
        if (!this.listenersAttached) return;
        window.removeEventListener('mousemove', this._onMouseMove);
        window.removeEventListener('mousedown', this._onMouseDown);
        window.removeEventListener('mouseup', this._onMouseUp);
        window.removeEventListener('keydown', this._onKeyDown);
        this.listenersAttached = false;
    }

    // =====================================
    // MOUSE DOWN + MOVE + UP HANDLING
    // =====================================

    private onMouseDown(event: MouseEvent): void {
        if (this.isUiEvent(event)) {
            this.mouseDownFromUi = true;
            return;
        }

        this.mouseDownFromUi = false;
        this.mouseDownPos = { x: event.clientX, y: event.clientY };
        this.movedTooMuch = false;
    }

    private onMouseUp(event: MouseEvent): void {
        if (this.mouseDownFromUi || this.isUiEvent(event)) {
            this.mouseDownFromUi = false;
            this.mouseDownPos = null;
            this.movedTooMuch = false;
            return;
        }

        // If user dragged camera, skip adding point
        if (this.movedTooMuch) {
            this.mouseDownPos = null;
            this.movedTooMuch = false;
            return;
        }

        // Convert to normalized device coords for raycaster
        this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        // Treat as click only if no drag
        this.onClick(event);
        this.mouseDownPos = null;
        this.movedTooMuch = false;
    }

    onMouseMove(event: MouseEvent): void {
        if (this.mouseDownFromUi || this.isUiEvent(event)) {
            return;
        }

        // Track movement distance
        if (this.mouseDownPos) {
            const dx = event.clientX - this.mouseDownPos.x;
            const dy = event.clientY - this.mouseDownPos.y;
            const distSq = dx * dx + dy * dy;
            if (distSq > this.moveThreshold * this.moveThreshold) {
                this.movedTooMuch = true;
            }
        }

        if (!this.drawModule.running) return;

        this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        if (this.drawModule.running && this.drawModule.points.length >= this.drawModule.minNumPoints) {
            this.handleCloseRadiusIndicator();
        }
    }

    // =====================================
    // CLOSE-RADIUS INDICATOR HANDLER
    // =====================================

    handleCloseRadiusIndicator(): void {
        const activeCamera = this.getActiveCamera();
        if (!activeCamera) return;
        this.raycaster.setFromCamera(this.mouse, activeCamera);
        const ground: THREE.Object3D[] = getGrid().getActiveGeodata().dataObjectLegacy?.Ground?.trianglesMesh.children ?? [];

        // 0. Reset visuals:
        this.drawModule.visualEffects.unhighlightAllSpheres();
        this.drawModule.visualEffects.hideCloseRadiusIndicator();
        this.drawModule.visualEffects.clearSegmentHighlight();

        // 1. Compute intersections:
        const intersects = this.raycaster.intersectObjects(ground);
        if (intersects.length === 0) {
            return;
        }

        // 2. Get index of the point near the mouse (if any):
        const intersection = intersects[0];
        const mousePosition = new FVector(intersection.point.x, -intersection.point.z, intersection.point.y);
        const hoverIndex = GeometryUtils.findPointNearClick(mousePosition, this.drawModule.points, this.drawModule.pointClickThreshold);

        // 3. Highlight Hovered point (if any):
        if (hoverIndex !== null) {
            this.drawModule.visualEffects.highlightSphere(hoverIndex);

            // If first point, show pulsing close indicator:
            if (hoverIndex === 0 && this.drawModule.points.length >= this.drawModule.minNumPoints && !this.drawModule.editing) {
                this.drawModule.visualEffects.showCloseRadiusIndicator(this.drawModule.points[0]);
            }

            // Don't highlight segment if point is highlighted (it has priority):
            return;
        }

        // 4. Get hovered segment:
        const segment = GeometryUtils.findLineSegmentNearClick(mousePosition, this.drawModule.points, this.newPointRadius);

        // 5. Highlight Hovered segment (if any):
        if (segment) {
            this.drawModule.visualEffects.highlightSegment(segment.segmentIndex);
        }
    }

    // =====================================
    // CLICK HANDLER (TRIGGERED AFTER UP)
    // =====================================

    onClick(event: MouseEvent): void {
        // Ignore all clicks if module is not in edit mode:
        if (!this.drawModule.running) return;
        if (this.isUiEvent(event)) return;

        // Ignore click used to exit from TransformControls:
        const now = Date.now();
        if (this.suppressNextClick && now < this.suppressUntil) {
            this.suppressNextClick = false;
            return;
        }

        // Convert mouse to NDC:
        this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        // Raycast ground:
        const activeCamera = this.getActiveCamera();
        if (!activeCamera) return;
        this.raycaster.setFromCamera(this.mouse, activeCamera);
        const ground: THREE.Object3D[] = getGrid().getActiveGeodata().dataObjectLegacy?.Ground?.trianglesMesh.children ?? [];
        const intersects = this.raycaster.intersectObjects(ground);
        if (intersects.length === 0) return;

        const intersection = intersects[0];
        const clickPoint = new FVector(intersection.point.x, -intersection.point.z, intersection.point.y);

        if (event.button === 0) this.handleLeftClick(clickPoint);
        else if (event.button === 2) this.handleRightClick(clickPoint);
    }

    private isUiEvent(event: MouseEvent): boolean {
        const target = event.target as HTMLElement | null;
        if (!target || !target.closest) return false;

        return Boolean(
            target.closest('.gui-drawer, .drawer-handle, .data-panel, [role="dialog"], .MuiPopover-root, .MuiMenu-paper, .loading-overlay')
        );
    }

    private getActiveCamera(): THREE.Camera | null {
        const viewer = getViewer();
        if (!viewer?.cameraHelper) return null;
        const cameraType = getGlobalState().ambient.currentCamera.type;
        return viewer.cameraHelper.getActiveCamera(cameraType);
    }

    handleLeftClick(clickPoint: FVector): void {
        const now = Date.now();
        this.lastClickTime = now;

        const nearPointIndex = GeometryUtils.findPointNearClick(clickPoint, this.drawModule.points, this.drawModule.pointClickThreshold);

        // 1. Close polygon (Single-click near first point)
        if (nearPointIndex === 0 && this.drawModule.points.length >= this.drawModule.minNumPoints && !this.drawModule.editing) {
            this.drawModule.visualEffects.hideCloseRadiusIndicator();
            this.drawModule.stop();
            return;
        }

        // 2) Select & move existing point (except first point)
        if (nearPointIndex !== null) {
            if (nearPointIndex === 0 && !this.drawModule.editing) {
                // First point is not movable — do nothing --> it is movable in edit.
                return;
            }

            // Activate transform controls on single click
            this.activateTransformControls(nearPointIndex);
            return;
        }

        // 3. Insert point on edge
        const lineInsertionResult = GeometryUtils.findLineSegmentNearClick(
            clickPoint,
            this.drawModule.points,
            this.drawModule.pointClickThreshold
        );
        if (lineInsertionResult !== null) {
            this.drawModule.insertPointOnLine(lineInsertionResult.segmentIndex, clickPoint);
            return;
        }

        // 4. Add new point if editing
        if (this.drawModule.running && !this.drawModule.editing) {
            this.drawModule.addPoint(clickPoint);
        }
    }

    private handleRightClick(clickPoint: FVector): void {
        const nearPointIndex = GeometryUtils.findPointNearClick(clickPoint, this.drawModule.points, this.drawModule.pointClickThreshold);

        if (nearPointIndex !== null) {
            this.activateTransformControls(nearPointIndex);
        } else if (getViewer().transformControls.enabled) {
            this.deactivateTransformControls();
        }
    }

    // =====================================
    // KEYBOARD HANDLER
    // =====================================

    onKeyDown(event: KeyboardEvent): void {
        if (
            (event.key === 'Delete' || event.key === 'Backspace') &&
            this.drawModule.running &&
            getViewer().transformControls.enabled &&
            this.drawModule.selectedPointIndex !== null
        ) {
            event.preventDefault();
            this.drawModule.deletePoint(this.drawModule.selectedPointIndex);
        }
    }

    // =====================================
    // TRANSFORM CONTROLS
    // =====================================

    activateTransformControls(pointIndex: number): void {
        if (pointIndex < 0 || pointIndex >= this.drawModule.points.length) {
            console.warn(`Invalid point index: ${pointIndex}, valid range: 0-${this.drawModule.points.length - 1}`);
            return;
        }

        // 0) Reset everything before reactivating:
        this.deactivateTransformControls();
        this.drawModule.selectedPointIndex = pointIndex;
        this.drawModule.transformControlsActive = true;

        const originalPosition = this.drawModule.points[pointIndex].clone();

        // 1) Add helper to scene:
        const helper = new THREE.Object3D();
        helper.position.set(originalPosition.x, originalPosition.y, originalPosition.z);
        getViewer().scene.add(helper);

        // 2) Force correct world Matrix:
        helper.updateMatrixWorld(true);

        // 3) Attach TransformControls:
        this.drawModule.transformHelperObject = helper;

        const tc = getViewer().transformControls;
        tc.detach();
        tc.attach(helper);

        // 4) Update TransformControls Colors:
        tc.setColors(
            0xff0000, // X
            0x00ff00, // Y
            0x00ff00, // Z
            0xffff00 // active
        );

        tc.setSpace('local');
        tc.showZ = false;
        tc.enabled = true;

        const groundRaycaster = new THREE.Raycaster();

        this.onTransformChange = () => {
            if (
                this.drawModule.selectedPointIndex !== null &&
                this.drawModule.transformHelperObject &&
                this.drawModule.selectedPointIndex < this.drawModule.points.length
            ) {
                const transformedPosition = this.drawModule.transformHelperObject.position.clone();
                const groundConstrainedPosition = GeometryUtils.projectToGround(transformedPosition, groundRaycaster);
                if (groundConstrainedPosition) {
                    this.drawModule.transformHelperObject.position.copy(groundConstrainedPosition);
                    this.drawModule.points[this.drawModule.selectedPointIndex].copy(groundConstrainedPosition);

                    const spheres = this.drawModule.visualEffects.getSpheres();
                    if (spheres && spheres[this.drawModule.selectedPointIndex]) {
                        spheres[this.drawModule.selectedPointIndex].position.copy(groundConstrainedPosition);
                    }

                    // Refresh visuals:
                    this.drawModule.visualEffects.removePolyLine();
                    const closedPoints = [...this.drawModule.points, this.drawModule.points[0]];
                    this.drawModule.visualEffects.generatePolyLine(this.drawModule.editing ? closedPoints : this.drawModule.points);

                    // Notify React:
                    this.drawModule.emit('pointsChanged', this.drawModule.getPolygonPositions());
                }
            }
        };

        this.onTransformEnd = () => {
            if (this.drawModule.selectedPointIndex === null) return;

            const idx = this.drawModule.selectedPointIndex;
            const newPos = this.drawModule.points[idx];
            const constraintFn = this.drawModule.isInsidePolygon.bind(this.drawModule); // bind = uses drawModule "this" in the function and not EventHandler's.

            // Inside Polygon Check:
            // Shows fading sphere and moves transform to og pos if the point has been moved outside of defined polygon:
            if (!constraintFn(newPos)) {
                this.drawModule.visualEffects.showInvalidClickIndicator?.(newPos);
                this.drawModule.onInvalidPoint?.();

                newPos.copy(originalPosition);
                this.drawModule.transformHelperObject.position.copy(originalPosition);

                const spheres = this.drawModule.visualEffects.getSpheres();
                if (spheres && spheres[idx]) spheres[idx].position.copy(originalPosition);
            }

            // Self-intersection check:
            if (this.drawModule.onSelfIntersection) {
                const prospective = [...this.drawModule.points];
                prospective[idx] = newPos.clone();

                const fVecs = prospective.map((p) => new FVector(p.x, p.y, p.z));
                if (polygonHasSelfIntersection(fVecs)) {
                    this.drawModule.visualEffects.showInvalidClickIndicator?.(newPos);
                    this.drawModule.onSelfIntersection?.();

                    newPos.copy(originalPosition);
                    this.drawModule.transformHelperObject.position.copy(originalPosition);

                    const spheres = this.drawModule.visualEffects.getSpheres();
                    if (spheres && spheres[idx]) spheres[idx].position.copy(originalPosition);
                }
            }

            // Refresh visuals & deactivate:
            this.drawModule.visualEffects.removePolyLine();

            const closedPoints = [...this.drawModule.points, this.drawModule.points[0]];
            this.drawModule.visualEffects.generatePolyLine(this.drawModule.editing ? closedPoints : this.drawModule.points);

            // Notify React:
            this.drawModule.emit('pointsChanged', this.drawModule.getPolygonPositions());

            this.deactivateTransformControls();
        };

        getViewer().transformControls.addEventListener('change', this.onTransformChange);
        getViewer().transformControls.addEventListener('mouseUp', this.onTransformEnd);
    }

    deactivateTransformControls(): void {
        if (!this.drawModule.transformControlsActive) return;
        if (!getViewer().transformControls.enabled) return;

        if (this.onTransformChange) {
            getViewer().transformControls.removeEventListener('change', this.onTransformChange);
            this.onTransformChange = null;
        }

        if (this.onTransformEnd) {
            getViewer().transformControls.removeEventListener('mouseUp', this.onTransformEnd);
            this.onTransformEnd = null;
        }

        getViewer().transformControls.detach();
        getViewer().transformControls.enabled = false;

        if (this.drawModule.transformHelperObject) {
            getViewer().scene.remove(this.drawModule.transformHelperObject);
            this.drawModule.transformHelperObject = null;
        }

        this.drawModule.selectedPointIndex = null;
    }
}
